<template>
  <config-base class="zcm-storage-config">
    <div slot="config">
      <zm-radio-group
        v-model="selectedPlanCode"
        enter-select
        aria-label="Select a capacity"
        class="opc-card-radio zcm-storage-config__radio-group"
        @change="handlePlanCodeChange"
      >
        <zm-radio v-for="plan in planList" :key="plan.plan_code" :label="plan.plan_code">
          <p class="zcm-storage-config__radio-group__capacity">{{ plan.cartCapacity }} {{ plan.cartCapacityUnit }}</p>
          <p>
            <span class="zcm-storage-config__radio-group__price">
              {{ $money(plan.amount, { forceDiff: true }) }}
            </span>
            <span class="zcm-storage-config__radio-group__cycle">
              {{ $t(`billing.common.per_billing_cycle.${plan.billing_cycle}`) }}
            </span>
          </p>
        </zm-radio>
      </zm-radio-group>
      <preview-payment class="gap24" />
    </div>
    <template #footer v-if="isShowRemoveBtn">
      <zm-button type="link" ghost @click="removePlanByKey" :aria-label="`remove ${title} plan`">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import ConfigBase from '@/components/config-base.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import { plansof } from '@/utils/chain';
import { translateToEn } from '@/plugins/i18n';
import plansMixin from '../mx/plan';
import commonMixin from './mixin';

const ConfigBaseComp = {
  name: 'ZCMStorageBase',
  extends: ConfigBase,
  mixins: [commonMixin],
  data() {
    return {
      showLearnMore: false,
      showPlanDetails: false,
    };
  },
  methods: {
    /**
     * override ConfigBase
     * @param {object} plan
     */
    mx_renderCapacity(plan) {
      if (!plan?.capacity) return;
      return (
        <li>
          {plan?.cartCapacity} {plan?.cartCapacityUnit}
        </li>
      );
    },

    /**
     * override ConfigBase
     */
    mx_renderLicense() {
      return null;
    },

    /**
     * override ConfigBase
     * @param {object} plan
     */
    mx_renderCycle(plan) {
      return (
        <li>
          {this.$money(plan.amount, { forceDiff: true })}
          {this.$t(`billing.common.per_billing_cycle.${plan.billing_cycle}`)}
        </li>
      );
    },
  },
};

export default {
  name: 'ZCMStorageConfig',
  mixins: [commonMixin, plansMixin],
  components: { ConfigBase: ConfigBaseComp, PreviewPayment },
  data() {
    return {
      selectedPlanCode: '',
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'planMapFlat']),

    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },

    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType);
    },

    planTrackingName() {
      return translateToEn(this.selectedPlan?.planNameKey);
    },
  },
  watch: {
    // update selected plan
    selectedPlanCode(newCode, oldCode) {
      if (oldCode) {
        this.switchSelectPlan([oldCode, newCode, undefined]);
      } else {
        this.updateSelectPlan({
          plan_code: newCode,
          cartInfo: { selected_number: 1 },
        });
      }
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchSelectPlan']),

    /**
     * plan code changed
     * @param {string} planCode
     */
    handlePlanCodeChange(planCode) {
      const plan = this.planList.find((plan) => plan.plan_code === planCode);
      if (plan) {
        this.$tracking.buyInteraction(`${this.planTrackingName} plan`, `${plan.cartCapacity}${plan.cartCapacityUnit}`);
      }
    },
  },

  created() {
    if (this.selectedPlan) {
      this.selectedPlanCode = this.selectedPlan.plan_code;
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/zoom-card.scss';
.gap24 {
  margin-top: 24px;
}

.zcm-storage-config {
  /deep/ .opc-zm-base-card {
    &__name {
      text-transform: none;
    }
    &__main--desc__content {
      font-size: 14px;
      color: #666487;
    }
  }

  &__radio-group {
    flex-wrap: wrap;
    /deep/ .zm-radio {
      width: calc(50% - 8px);
      @include media(mobile) {
        width: 100%;
      }
      &__wrap {
        width: 100%;
      }
      &__label--inner {
        width: 100%;
      }
    }
    &__capacity {
      font-size: 16px;
      font-weight: 600;
      color: #00053d;
    }
    &__price {
      font-size: 22px;
      font-weight: 700;
      color: #00053d;
    }
    &__cycle {
      font-size: 12px;
      font-weight: 500;
      color: #515079;
    }
  }
}
</style>
